import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/utils";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SUBTYPES = ["Infrared", "Traditional", "Barrel", "Cabin"];
const PLACEMENTS = ["indoor", "outdoor"];
const CAPACITIES = [["1-2", "1–2 people"], ["3-4", "3–4 people"], ["5+", "5+ people"], ["6+", "Commercial 6+"]];
const FEATURES = ["Chromotherapy", "Bluetooth", "WiFi", "Digital Controls"];
const BRANDS = ["Harvia", "Kohler", "Dundalk LeisureCraft", "HealthSmart", "The Recovery Shops"];

export default function Shop() {
  const { category } = useParams();
  const [sp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [filters, setFilters] = useState({ subtype: [], placement: [], capacity: [], feature: [], brand: [] });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (sort) params.sort = sort;
    if (sp.get("commercial")) params.commercial = true;
    api.getProducts(params).then((d) => {
      setProducts(d.products || []);
      setLoading(false);
    });
  }, [category, sort, sp]);

  const toggle = (group, val) => {
    setFilters((f) => {
      const arr = f[group].includes(val) ? f[group].filter((x) => x !== val) : [...f[group], val];
      return { ...f, [group]: arr };
    });
  };

  const filtered = products.filter((p) => {
    if (filters.subtype.length && !filters.subtype.includes(p.subtype)) return false;
    if (filters.placement.length && !filters.placement.some((x) => [p.placement, "both"].includes(x))) return false;
    if (filters.capacity.length && !filters.capacity.includes(p.capacity)) return false;
    if (filters.feature.length && !filters.feature.every((x) => (p.features || []).includes(x))) return false;
    if (filters.brand.length && !filters.brand.includes(p.brand)) return false;
    return true;
  });

  const showSubtype = !category || category === "sauna";
  const title = category ? CATEGORY_LABELS[category] || "Shop" : "Shop All";

  const FilterPanel = () => (
    <div className="space-y-2" data-testid="shop-filters">
      <Accordion type="multiple" defaultValue={["brand", "type"]} className="text-cream">
        {showSubtype && (
          <AccordionItem value="type" className="border-white/10">
            <AccordionTrigger className="text-cream hover:text-gold">Sauna Type</AccordionTrigger>
            <AccordionContent className="space-y-2">
              {SUBTYPES.map((s) => <FilterRow key={s} group="subtype" val={s} label={s} />)}
            </AccordionContent>
          </AccordionItem>
        )}
        <AccordionItem value="placement" className="border-white/10">
          <AccordionTrigger className="text-cream hover:text-gold">Placement</AccordionTrigger>
          <AccordionContent className="space-y-2">
            {PLACEMENTS.map((s) => <FilterRow key={s} group="placement" val={s} label={s[0].toUpperCase() + s.slice(1)} />)}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="capacity" className="border-white/10">
          <AccordionTrigger className="text-cream hover:text-gold">Capacity</AccordionTrigger>
          <AccordionContent className="space-y-2">
            {CAPACITIES.map(([v, l]) => <FilterRow key={v} group="capacity" val={v} label={l} />)}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="feature" className="border-white/10">
          <AccordionTrigger className="text-cream hover:text-gold">Features</AccordionTrigger>
          <AccordionContent className="space-y-2">
            {FEATURES.map((s) => <FilterRow key={s} group="feature" val={s} label={s} />)}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="brand" className="border-white/10">
          <AccordionTrigger className="text-cream hover:text-gold">Brand</AccordionTrigger>
          <AccordionContent className="space-y-2">
            {BRANDS.map((s) => <FilterRow key={s} group="brand" val={s} label={s} />)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );

  const FilterRow = ({ group, val, label }) => (
    <label className="flex items-center gap-2 text-sm text-cream/80 cursor-pointer hover:text-cream">
      <Checkbox data-testid={`filter-${group}-${val}`} checked={filters[group].includes(val)}
        onCheckedChange={() => toggle(group, val)} className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
      {label}
    </label>
  );

  return (
    <div className="pt-16" data-testid="shop-page">
      <div className="bg-charcoal border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
          <p className="overline">Collection</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mt-2">{title}</h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block">
          <FilterPanel />
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6">
            <button data-testid="mobile-filter-toggle" onClick={() => setShowFilters(true)}
              className="lg:hidden flex items-center gap-2 text-cream border border-white/15 px-3 py-2 text-sm">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <p className="text-cream/60 text-sm">{filtered.length} products</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-44 bg-charcoal border-white/15 text-cream">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/15 text-cream">
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-cream/60 py-20 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-cream/60 py-20 text-center" data-testid="shop-empty">No products match your filters.</div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-espresso p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-cream">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X className="text-cream" /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setShowFilters(false)} className="w-full mt-6 bg-gold text-espresso py-3 font-medium">Show {filtered.length} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
